// components/LocationSelector.tsx
import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  SelectChangeEvent,
  Alert,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearLocations, fetchLocationsByGdmNumber } from '@/store/slices/locationSlice';
import { setSelectedLocation } from '@/store/slices/selectionSlice';
import {
  selectLocations,
  selectLocationLoading,
  selectLocationError,
  selectSelectedSiteId,
  selectSelectedLocationId,
  selectSelectedLocationName,
  selectSelectedSiteObject,
} from '@/store/selectors';
import { useTranslation } from "react-i18next";
import { RootState } from '@/store/store';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import MiniLoader from '../MiniLoader';
import { Site } from '@/store/slices/siteSlice';
import { LocationDetails } from '@/types/customers';


interface LocationSelectorProps {
  label?: string;
  fullWidth?: boolean;
  disabled?: boolean;
}

export const LocationSelector: React.FC<LocationSelectorProps> = React.memo(({
  label = 'Select Location',
  fullWidth = true,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { getAccessTokenSilently } = useAuth0();

  const locations = useAppSelector(selectLocations);
  const loading = useAppSelector(selectLocationLoading);
  const error = useAppSelector(selectLocationError);
  const selectedSiteId = useAppSelector(selectSelectedSiteId);
  const selectedLocationId = useAppSelector(selectSelectedLocationId);
  const selectedLocationName = useAppSelector(selectSelectedLocationName);
  const selectedSiteObject = useAppSelector(selectSelectedSiteObject);
  const { data: userData } = useAppSelector((state) => state.user);
  const isDeviceDetailPage = pathname.startsWith("/devices/");
  const isSearchResultsPage = pathname.startsWith("/search-results/");
  const isWalkdownDetailPage = /^\/walkdowns\/(?!qr-codes)[^/]+$/.test(pathname);
  const isTurnAroundDetailPage = /^\/turnarounds\/[^/]+$/.test(pathname);
  const freezeForm = isDeviceDetailPage || isSearchResultsPage || isWalkdownDetailPage || isTurnAroundDetailPage;
  const [dialogOpen, setDialogOpen] = useState(false);
  const closeDialog = () => setDialogOpen(false);
  const [expanded, setExpanded] = useState<string[]>([]);

  // Fetch locations when site changes
  useEffect(() => {

    async function run() {
      if (selectedSiteId && dialogOpen && selectedSiteObject?.gdm_customer_number) {
        console.log('LocationSelector: fetching locations', selectedSiteId);
        const accessToken = await getAccessTokenSilently();
        dispatch(fetchLocationsByGdmNumber({
          gdmNumber: selectedSiteObject.gdm_customer_number,
          userGuid: userData?.id || '',
          accessToken
        }));
      } else {
        // Clear locations when no site is selected
        dispatch(clearLocations());
      }
    }

    run();
  }, [selectedSiteId, dialogOpen, selectedSiteObject?.gdm_customer_number, dispatch]);

  const locationList = useMemo(() => {
    return locations || [];
  }, [locations]);

  const handleChange = useCallback((event: SelectChangeEvent<string>) => {
    const locationId = event.target.value || null;
    // dispatch(setSelectedLocation(selectedLocationId));
  }, [dispatch]);

  const getActionColor = (activeColor: string) =>
    freezeForm ? "#999" : activeColor;

  const handleManualSelection = (callback: () => void) => {
    // dispatch(setManualSelection(true));
    callback();
  };

  const handleLocationSelect = () => {
    setDialogOpen(true);
  };

  const handleRemoval = () => {
    dispatch(clearLocations());
    dispatch(setSelectedLocation(null));
  }

  const CustomTreeNode: React.FC<{
    node: LocationDetails;
    level: number;
  }> = ({ node, level }) => {
    const isExpanded = expanded.includes(node.id as string);
    const hasChildren = node.children && node.children.length > 0;
    const isLeaf = !hasChildren;
    const childCount = node.children?.length || 0;

    const handleArrowClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (hasChildren) {
        handleToggle(node.id as string);
      }
    };

    const handleNodeClick = () => {
      // Select the node when clicked (both parent and leaf nodes can be selected)
      dispatch(setSelectedLocation({ id: node.id as string, name: node.name as string }));
      closeDialog();
    };

    return (
      <div className="custom-tree-node">
        <div
          className="custom-tree-item"
          style={{ paddingLeft: `${level * 20}px` }}
          onClick={handleNodeClick}
        >
          <div className="custom-tree-item-content">
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={handleArrowClick}
                className="custom-tree-arrow"
              >
                {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>
            ) : (
              <div className="custom-tree-spacer" />
            )}
            <div className="custom-tree-label">
              <Typography className="treeItemName">{node.name}</Typography>
              <Typography className="treeItemCount">({childCount})</Typography>
            </div>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="custom-tree-children">
            {node.children?.map((child: LocationDetails) => (
              <CustomTreeNode
                key={child.id}
                node={child}
                level={level + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleToggle = (nodeId: string) => {
    setExpanded(prev =>
      prev.includes(nodeId)
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };

  return (
    <>
      <Box className="csgf-row">
        <Typography className="csgf-label-site">
          {t("location", "LOCATION")}
        </Typography>
        {selectedLocationId ? (
          <Typography
            className="csgf-action"
            style={{
              color: getActionColor("#00805A"),
              cursor: freezeForm ? "not-allowed" : "pointer",
            }}
          >
            <span
              onClick={() => {
                if (freezeForm) return;
                handleLocationSelect();
              }}
            >
              {t("change", "Change")}
            </span>
            <span className="csgf-separator">|</span>
            <span
              onClick={() => {
                if (freezeForm) return;
                handleRemoval();
              }}
            >
              {t("remove", "Remove")}
            </span>
          </Typography>
        ) : (
          <Typography
            className={`csgf-action ${freezeForm || !selectedSiteId
              ? "csgf-disabled"
              : "csgf-enabled"
              }`}
            onClick={() => {
              if (freezeForm || !selectedSiteId)
                return;
              handleLocationSelect();

            }}
          >
            {t("select", "Select")}
          </Typography>
        )}
      </Box>

      {selectedLocationId ? (
        <Typography className="csgf-location">
          {selectedLocationName}
        </Typography>
      ) : (
        <Typography className="csgf-none">
          {t("noneSelected", "None Selected")}
        </Typography>
      )}

      {/* Location Select Dialog */}
      {
        (dialogOpen) && (
          <Dialog
            open={dialogOpen}
            onClose={closeDialog}
            maxWidth={"sm"}
            fullWidth
          >
            <DialogTitle className="dialogTitle">
              {t("selectLocation", "SELECT LOCATION")}
              <IconButton onClick={closeDialog} className="closeButton" aria-label="Close">
                <CloseIcon className="closeIcon" />
              </IconButton>
            </DialogTitle>

            <DialogContent
              dividers
              className={"dialogContentLocation"}
            >
              {(loading) && (
                <Box className="loaderOverlay">
                  <MiniLoader size={32} />
                </Box>
              )}

              {error && (
                <Typography className="locationsError">
                  {t(
                    "errorLoadingLocations",
                    "Failed to load locations. Please try again."
                  )}
                </Typography>
              )}

              {(
                <>
                  <Typography className="locationsHeading">
                    {selectedSiteObject?.site_name} Locations
                  </Typography>
                  {locations?.length === 0 ? (
                    <Typography className="noLocations">
                      {t(
                        "noLocationsAvailable",
                        "No locations available for this site"
                      )}
                    </Typography>
                  ) : (
                    <div className="custom-tree-container">
                      {locations?.map((node: LocationDetails) => (
                        <CustomTreeNode
                          key={node.id}
                          node={node}
                          level={0}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </DialogContent>
          </Dialog>
        )
      }

    </>
  );
});

LocationSelector.displayName = 'LocationSelector';
export default LocationSelector;